import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { parseSecondsToMinutes, renderToolTip } from "../../../../../../../../config/functions";
import { isMotorProtocol, isCognitiveProtocol } from "../../../../../../../../config/validateProtocols";
import { GetAppBasicProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/appBasics";
import { GetAppRotationProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/appRotation";
import { GetAppARProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/appAR";
import { GetHandTrackingProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/webHandTracking";
import { GetFullBodyProtocolData } from "../../../../../../../prescriptions/sections/getProtocolData/webFullBody";
// Components
import {
  PrescriptionDiv,
  ProtocolCardDiv,
  VideoButton,
  ProtocolImgDiv,
  ProtocolImg,
  VideoButtonImg,
  InfoImg,
  ProtocolName,
  PrescriptionDaysDiv,
  PrescriptionDaysAligner,
  DayText,
  InputCheckboxDiv,
  InputCheckbox,
  FunctionsDiv,
  FunctionDiv,
  FunctionText,
  ValueDiv,
  ValueText,
  DurationDiv,
  DurationSelect,
  StatusDiv,
  StatusText,
} from "./styles";
import ProtocolVideoPopup from "../../../../../../../protocolVideoPopup";
// Assets
import IcoOpenVideo from "../../../../../../../../assets/icons/ico_video.svg";
import IcoInfo from "../../../../../../../../assets/icons/ico_info_blue.svg";

const ProtocolPrescription = ({
  protocolId,
  prescriptionDuration,
  prescriptionDays = [],
  dmValue,
  adherenceValue,
  competenceValue,
  sessionsNumber,
  status = "NORMAL",
}) => {
  const [protocolImg, setProtocolImg] = useState(null);
  const [protocolName, setProtocolName] = useState(null);
  const [protocolDescription, setProtocolDescription] = useState(null);
  const [protocolVideo, setProtocolVideo] = useState(null);
  // Days
  const [monday, setMonday] = useState(false);
  const [tuesday, setTuesday] = useState(false);
  const [wednesday, setWednesday] = useState(false);
  const [thursday, setThursday] = useState(false);
  const [friday, setFriday] = useState(false);
  const [saturday, setSaturday] = useState(false);
  const [sunday, setSunday] = useState(false);

  const [duration] = useState(parseSecondsToMinutes(prescriptionDuration) || 5);

  const [isProtocolVideoShowed, setIsProtocolVideoShowed] = useState(false);
  const [t] = useTranslation("global");

  // Setting prescription days
  useEffect(() => {
    const daysToSet = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

    const setDays = (weekday, setDay) => {
      const filteredDay = prescriptionDays.filter((prescriptionDay) => prescriptionDay === weekday);
      if (filteredDay.length) setDay(true);
    };

    daysToSet.forEach((day, index) => {
      setDays(day, [setMonday, setTuesday, setWednesday, setThursday, setFriday, setSaturday, setSunday][index]);
    });
  }, [prescriptionDays]);

  // Setting protocol info
  useEffect(() => {
    const getProtocolDataFunctions = [
      GetAppBasicProtocolData,
      GetAppRotationProtocolData,
      GetAppARProtocolData,
      GetHandTrackingProtocolData,
      GetFullBodyProtocolData,
    ];

    for (const getProtocolData of getProtocolDataFunctions) {
      const protocolData = getProtocolData(protocolId);
      if (protocolData) {
        setProtocolImg(protocolData.img);
        setProtocolName(t(`protocols_${protocolData.name}`));
        setProtocolDescription(t(`protocols_${protocolData.description}`));
        setProtocolVideo(t(`protocols_${protocolData.video}`));
      }
    }
  }, [protocolId, t]);

  const renderProtocolCard = () => {
    return (
      <ProtocolCardDiv>
        <VideoButton onClick={() => setIsProtocolVideoShowed(true)} disabled={!protocolVideo}>
          <ProtocolImgDiv height={status === "NORMAL" ? "40px" : "38px"}>
            <ProtocolImg src={protocolImg} alt="protocol-img" />
          </ProtocolImgDiv>
          {protocolVideo ? (
            <VideoButtonImg src={IcoOpenVideo} alt="ico-video" data-for="protocolDescription" data-tip={protocolDescription} />
          ) : (
            <InfoImg src={IcoInfo} alt="ico-info" data-for="protocolDescription" data-tip={protocolDescription} />
          )}
          {renderToolTip("protocolDescription")}
          <ProtocolName>{protocolName}</ProtocolName>
        </VideoButton>
        {isProtocolVideoShowed && <ProtocolVideoPopup setIsProtocolVideoShowed={setIsProtocolVideoShowed} protocolId={protocolId} />}
      </ProtocolCardDiv>
    );
  };

  const renderPrescriptionDays = () => {
    const days = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

    const dayValues = {
      MONDAY: monday,
      TUESDAY: tuesday,
      WEDNESDAY: wednesday,
      THURSDAY: thursday,
      FRIDAY: friday,
      SATURDAY: saturday,
      SUNDAY: sunday,
    };

    return (
      <PrescriptionDaysDiv>
        <PrescriptionDaysAligner>
          {days.map((day) => {
            const dayValue = dayValues[day];
            const id = `${day}${protocolId}`;

            const getBorderRadius = () => {
              if (day === "MONDAY") return "30px 0 0 30px";
              if (day === "SUNDAY") return "0 30px 30px 0";
              return "0";
            };

            return (
              <InputCheckboxDiv key={id} borderRadius={getBorderRadius()} background={dayValue ? "#39B54A" : "#E6E6E6"}>
                <InputCheckbox id={id} type="checkbox" value={dayValue} disabled />
                <DayText color={dayValue ? "#FFF" : "#808080"}>{t(`prescriptions_tableProtocolDays_${day.toLowerCase()}`)}</DayText>
              </InputCheckboxDiv>
            );
          })}
        </PrescriptionDaysAligner>
      </PrescriptionDaysDiv>
    );
  };

  const renderProtocolFunctions = () => {
    return (
      <FunctionsDiv>
        {isMotorProtocol(protocolId) && (
          <FunctionDiv background="#D3F0E3">
            <FunctionText color="#22B573">MOTOR</FunctionText>
          </FunctionDiv>
        )}
        {isCognitiveProtocol(protocolId) && (
          <FunctionDiv background="#FCDED3">
            <FunctionText color="#F15A24">COGNITIVE</FunctionText>
          </FunctionDiv>
        )}
      </FunctionsDiv>
    );
  };

  const renderDMValue = () => {
    const getDM = () => {
      if (!dmValue || !Number(dmValue)) return "-";
      if (dmValue.length > 4) return Number(dmValue).toFixed(2);
      return dmValue;
    };

    return (
      <ValueDiv>
        <ValueText>{getDM()}</ValueText>
      </ValueDiv>
    );
  };

  const renderAdherenceValue = () => {
    const getAdherence = () => {
      if (!adherenceValue || !Number(adherenceValue)) return "-";
      return `${Math.round(Number(adherenceValue) * 100)}%`;
    };

    return (
      <ValueDiv>
        <ValueText>{getAdherence()}</ValueText>
      </ValueDiv>
    );
  };

  const renderCompetenceValue = () => {
    const getCompetence = () => {
      if (!competenceValue || !Number(competenceValue)) return "-";
      if (competenceValue.length > 4) return Number(competenceValue).toFixed(2);
      return competenceValue;
    };
    return (
      <ValueDiv>
        <ValueText>{getCompetence()}</ValueText>
      </ValueDiv>
    );
  };

  const renderDurationOptions = () => {
    const minutes = Array.from({ length: 3 }, (_, i) => {
      const min = i + 3;
      return (
        <option key={min} value={min}>
          {min} min
        </option>
      );
    });

    return (
      <DurationDiv>
        <DurationSelect value={duration} disabled>
          {minutes}
        </DurationSelect>
      </DurationDiv>
    );
  };

  const renderSessionsNumber = () => {
    const getSessionNumber = () => {
      if (!sessionsNumber || !Number(sessionsNumber)) return "-";
      if (sessionsNumber > 99) return "+99";
      return sessionsNumber;
    };
    return (
      <ValueDiv width="30px">
        <ValueText>{getSessionNumber()}</ValueText>
      </ValueDiv>
    );
  };

  const renderStatus = () => {
    const isRemoved = status === "REMOVED";
    const rightSpace = isRemoved ? "-86px" : "-70px";
    const border = isRemoved ? "1.5px solid #C1272D" : "1.5px solid #26A9B6";
    const background = isRemoved ? "#F6DFE0" : "#DEF2F4";
    const color = isRemoved ? "#C1272D" : "#26A9B6";

    return (
      <StatusDiv rightSpace={rightSpace} border={border} background={background}>
        <StatusText color={color}>{isRemoved ? "REMOVED" : "ADDED"}</StatusText>
      </StatusDiv>
    );
  };

  const getBorder = () => {
    if (status === "REMOVED") return "1.5px solid #C1272D";
    if (status === "ADDED") return "1.5px solid #26A9B6";
    return "";
  };

  const getBackgroundColor = () => {
    if (status === "REMOVED") return "#fde4e5";
    if (status === "ADDED") return "#e5f5f6";
    return "#fff";
  };

  return (
    <PrescriptionDiv border={getBorder()} background={getBackgroundColor()}>
      {renderProtocolCard()}
      {renderPrescriptionDays()}
      {renderProtocolFunctions()}
      {renderDMValue()}
      {renderAdherenceValue()}
      {renderCompetenceValue()}
      {renderDurationOptions()}
      {renderSessionsNumber()}
      {status !== "NORMAL" && renderStatus()}
    </PrescriptionDiv>
  );
};

export default ProtocolPrescription;
